import { supabase } from '../../shared/supabase/supabaseClient';
import {
  createCartSnapshot,
  parseCartSnapshot,
} from '../application/posCartPersistence';
import type {
  PosCartPersistenceScope,
  PosCartPromoSnapshot,
  PosCartSnapshot,
} from '../domain/posParkedSaleTypes';
import type { CartItem } from '../store/usePosStore';

type RemoteDraftRow = {
  id: string;
  branch_id: string;
  payload: unknown;
  version: number;
  updated_at: string;
  expires_at: string;
};

type RemoteDraftResponse = {
  ok?: boolean;
  draft?: RemoteDraftRow | null;
};

export type RemoteSaleDraft = {
  id: string;
  snapshot: PosCartSnapshot;
  version: number;
  updatedAt: string;
  expiresAt: string;
};

function operationId(prefix: string): string {
  return `${prefix}:${crypto.randomUUID()}`;
}

function parseRemoteDraft(
  row: RemoteDraftRow,
  scope: PosCartPersistenceScope,
): RemoteSaleDraft | null {
  if (!row.id || !row.branch_id || !row.updated_at || !row.expires_at) return null;
  const snapshot = parseCartSnapshot(JSON.stringify(row.payload), scope, { applyTtl: false });
  if (!snapshot) return null;
  return {
    id: row.id,
    snapshot,
    version: row.version,
    updatedAt: row.updated_at,
    expiresAt: row.expires_at,
  };
}

export async function getActiveSaleDraft(
  scope: PosCartPersistenceScope,
): Promise<RemoteSaleDraft | null> {
  const { data, error } = await supabase.schema('pos').rpc('get_active_sale_draft', {
    p_branch_id: scope.branchId,
  });
  if (error) throw new Error(error.message);
  const row = (data as RemoteDraftResponse | null)?.draft;
  return row ? parseRemoteDraft(row, scope) : null;
}

export async function saveSaleDraft(input: {
  scope: PosCartPersistenceScope;
  cart: CartItem[];
  promo?: PosCartPromoSnapshot;
  replaceExisting?: boolean;
}): Promise<RemoteSaleDraft> {
  const snapshot = createCartSnapshot({
    scope: input.scope,
    cart: input.cart,
    promo: input.promo,
  });
  const { data, error } = await supabase.schema('pos').rpc('save_sale_draft', {
    p_payload: {
      branchId: input.scope.branchId,
      operationId: operationId('sale-draft-save'),
      replaceExisting: input.replaceExisting ?? false,
      snapshot,
    },
  });
  if (error) throw new Error(error.message);
  const result = data as { draft_id?: string; version?: number; expires_at?: string } | null;
  if (!result?.draft_id || !result.version || !result.expires_at) {
    throw new Error('El servidor no devolvió un draft válido.');
  }
  return {
    id: result.draft_id,
    snapshot,
    version: result.version,
    updatedAt: snapshot.savedAt,
    expiresAt: result.expires_at,
  };
}

export async function discardSaleDraft(input: {
  scope: PosCartPersistenceScope;
  draftId: string;
}): Promise<void> {
  const { data, error } = await supabase.schema('pos').rpc('discard_sale_draft', {
    p_payload: {
      branchId: input.scope.branchId,
      draftId: input.draftId,
      operationId: operationId('sale-draft-discard'),
    },
  });
  if (error) throw new Error(error.message);
  const result = data as { ok?: boolean } | null;
  if (result?.ok !== true) throw new Error('No se pudo descartar el draft remoto.');
}
